import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreatePositionFormDTO,
  ReplacePositionSalaryScaleDTO,
  UpdatePositionFormDTO,
} from "@/modules/positions/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { positionsApi } from "./client";

export const positionsKeys = {
  all: ["positions"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["positions", "list", q] as const,
  detail: (id: string) => ["positions", "detail", id] as const,
  salaryBands: (id: string) => ["positions", "salaryBands", id] as const,
};

export function usePositionsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: positionsKeys.list(q),
    queryFn: () => positionsApi.list(q),
  });
}

export function usePositionQuery(id: string) {
  return useQuery({
    queryKey: positionsKeys.detail(id),
    queryFn: () => positionsApi.get(id),
    enabled: !!id,
  });
}

export function usePositionSalaryBandsQuery(
  positionId: string,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: positionsKeys.salaryBands(positionId),
    queryFn: () => positionsApi.getSalaryBands(positionId),
    enabled: (opts?.enabled ?? true) && !!positionId,
  });
}

export function useReplacePositionSalaryBands(positionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReplacePositionSalaryScaleDTO) =>
      positionsApi.replaceSalaryBands(positionId, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: positionsKeys.salaryBands(positionId),
      });
    },
  });
}

export function useCreatePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePositionFormDTO) => positionsApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງຕໍາແໜ່ງສໍາເລັດ");
      qc.invalidateQueries({ queryKey: positionsKeys.all });
    },
    onError: () => toast.error("ສ້າງຕໍາແໜ່ງລົ້ມເຫຼວ"),
  });
}

export function useUpdatePosition(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePositionFormDTO) =>
      positionsApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂຕໍາແໜ່ງສໍາເລັດ");
      qc.invalidateQueries({ queryKey: positionsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: positionsKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂຕໍາແໜ່ງລົ້ມເຫຼວ"),
  });
}

export function useDeactivatePosition() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => positionsApi.deactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: positionsKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useReactivatePosition() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => positionsApi.reactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: positionsKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useDeletePosition() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => positionsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: positionsKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}
